import { ModifierType } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives additions, removals, and substitutions from a line item's selected
 * modifiers. Uses `parentId` to detect substitution pairs — when a modifier
 * has a `parentId` that matches another modifier's `id`, the parent is the
 * removed ingredient and the child is the added ingredient.
 *
 * For CYO products the app can return `sub`-typed modifiers without a parent
 * in the list — those represent ingredients picked into an empty slot and are
 * treated as additions.
 */
export function getLineItemModifications(params: Params): LineItemModifications {
  const { selectedModifiers, isCustomProduct } = params;

  const modifiersById = new Map(selectedModifiers.map(toIdEntry));

  const checkIsSubstitutionChild = createSubstitutionChildCheck(modifiersById);
  const checkIfParentHasIngredient = createParentIngredientCheck(modifiersById);

  const substitutions = selectedModifiers
    .filter(checkIfHasIngredient)
    .filter(checkIsSubstitutionChild)
    .filter(checkIfParentHasIngredient)
    .map(toSubstitutionModification(modifiersById));

  const substitutionChildren = selectedModifiers
    .filter(checkIfHasIngredient)
    .filter(checkIsSubstitutionChild);

  const parentIdsUsedInSubs = new Set(substitutionChildren.map(toParentId));

  const isNotParentUsedInSub = createParentUsedInSubCheck(parentIdsUsedInSubs);

  const standalone = selectedModifiers
    .filter(checkIfHasIngredient)
    .filter(isNotParentUsedInSub)
    .filter(createNotCheck(checkIsSubstitutionChild));

  const checkIfAddition = createAdditionCheck({ isCustomProduct });

  const additions = standalone.filter(checkIfAddition).map(toIngredientModification);
  const removals = standalone.filter(checkIfRemoval).map(toIngredientModification);

  return { additions, removals, substitutions };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function toIdEntry(modifier: SelectedModifier): [number, SelectedModifier] {
  return [modifier.id, modifier];
}

function checkIfHasIngredient(modifier: SelectedModifier): boolean {
  return Boolean(modifier.ingredient?.id);
}

function createParentIngredientCheck(modifiersById: ModifiersById) {
  return (modifier: SelectedModifier) =>
    checkIfHasIngredient(modifiersById.get(modifier.parentId!)!);
}

function createNotCheck(check: (modifier: SelectedModifier) => boolean) {
  return (modifier: SelectedModifier) => !check(modifier);
}

function createSubstitutionChildCheck(modifiersById: ModifiersById) {
  return (modifier: SelectedModifier) =>
    modifier.parentId != null && modifiersById.has(modifier.parentId);
}

function createParentUsedInSubCheck(parentIdsUsedInSubs: Set<number>) {
  return (modifier: SelectedModifier) => !parentIdsUsedInSubs.has(modifier.id);
}

function checkIfAdditionOrIncluded(modifier: SelectedModifier): boolean {
  return (
    modifier.modifierType === ModifierType.Addition ||
    modifier.modifierType === ModifierType.Included
  );
}

function checkIfSubstitution(modifier: SelectedModifier): boolean {
  return modifier.modifierType === ModifierType.Substitution;
}

function createAdditionCheck(params: CreateAdditionCheckParams) {
  return (modifier: SelectedModifier) => {
    if (checkIfAdditionOrIncluded(modifier)) return true;

    return params.isCustomProduct && checkIfSubstitution(modifier);
  };
}

function checkIfRemoval(modifier: SelectedModifier): boolean {
  return modifier.modifierType === ModifierType.Removal;
}

function toParentId(modifier: SelectedModifier): number {
  return modifier.parentId!;
}

function toIngredientModification(modifier: SelectedModifier): IngredientModification {
  return { ingredientId: modifier.ingredient!.id };
}

function toSubstitutionModification(modifiersById: ModifiersById) {
  return (modifier: SelectedModifier): SubstitutionModification => ({
    addedIngredientId: modifier.ingredient!.id,
    removedIngredientId: modifiersById.get(modifier.parentId!)!.ingredient!.id,
  });
}

// ─── Types ──────────────────────────────────────────────────────────────────

type ModifiersById = Map<number, SelectedModifier>;

type SelectedModifier = {
  id: number;
  parentId?: number | null;
  modifierType?: ModifierType | null;
  ingredient?: { id: string } | null;
};

type Params = {
  selectedModifiers: ReadonlyArray<SelectedModifier>;
  isCustomProduct: boolean;
};

type CreateAdditionCheckParams = {
  isCustomProduct: boolean;
};

type IngredientModification = {
  ingredientId: string;
};

type SubstitutionModification = {
  addedIngredientId: string;
  removedIngredientId: string;
};

type LineItemModifications = {
  additions: IngredientModification[];
  removals: IngredientModification[];
  substitutions: SubstitutionModification[];
};
